import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CheckboxComponent, IconComponent, ButtonComponent, ChipComponent, FileSelectorComponent } from '@kin/spark-ui';
import { AuthService } from '../auth/auth.service';
import { AddressService } from '../property/address.service';
import { PropertyAssessmentService } from '../property/property-assessment.service';
import { CaseService, TimelineStep, CaseDocument, CaseComment, NoticeOfAppealDocument, ConditionDefinition } from '../case.service';
import { COUNTY_COPY, County, getFilingDeadline, parseIsoDate } from '../county/county-config';
import { StepperComponent } from '../stepper/stepper.component';
import { PropertySidebarComponent } from '../property-sidebar/property-sidebar.component';

interface ConditionItem extends ConditionDefinition {
  checked: boolean;
  documents: CaseDocument[];
  uploadState: 'idle' | 'uploading' | 'error';
  uploadError: string;
}

@Component({
  selector: 'app-your-case',
  standalone: true,
  imports: [CommonModule, FormsModule, CheckboxComponent, IconComponent, ButtonComponent, ChipComponent, FileSelectorComponent, StepperComponent, PropertySidebarComponent],
  templateUrl: './your-case.component.html',
  styleUrl: './your-case.component.css',
})
export class YourCaseComponent implements OnInit {
  private router = inject(Router);
  private auth = inject(AuthService);
  protected addressService = inject(AddressService);
  protected assessment = inject(PropertyAssessmentService);
  private caseService = inject(CaseService);

  private get county(): County {
    return this.assessment.data?.property?.countyCode ?? 'harris';
  }

  caseNumber = '';
  copied = false;
  steps: TimelineStep[] = [];
  authorizedDate = '';
  expectedFilingDate = '';
  noteText = '';
  noteError = '';
  comments: CaseComment[] = [];
  appealNotice: NoticeOfAppealDocument | null = null;
  appealNoticeUploading = false;
  appealNoticeError = '';
  conditionsError = '';
  commentError = '';
  generalDocuments: CaseDocument[] = [];
  generalUploadState: 'idle' | 'uploading' | 'error' = 'idle';
  generalUploadError = '';

  get appealNoticeUploaded(): boolean {
    return this.appealNotice !== null;
  }

  /**
   * Recomputed on each access (rather than cached at construction) so it reflects the
   * live `county`, matching the pattern already used for `expectedFilingDate` in
   * `ngOnInit` — county-derived copy shouldn't be frozen at construction time.
   */
  get pageTitle(): string {
    return COUNTY_COPY[this.county].pageTitle;
  }

  get filingNoun(): string {
    return COUNTY_COPY[this.county].filingNoun;
  }

  conditions: ConditionItem[] = [];

  ngOnInit() {
    this.caseService.getCase().subscribe(data => {
      if (data) {
        this.caseNumber = data.caseNumber;
        this.steps = data.timeline;
        const created = new Date(data.createdAt);
        this.authorizedDate = created.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        const filing = new Date(created);
        filing.setDate(filing.getDate() + 7);
        // Prefer the real per-case deadline; only fall back to the county placeholder
        // when the backend doesn't have a real one yet (filingDeadline is null).
        const deadline = data.filingDeadline
          ? parseIsoDate(data.filingDeadline)
          : getFilingDeadline(this.county, created);
        const filingDate = filing > deadline ? deadline : filing;
        this.expectedFilingDate = filingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      }
    });
    this.loadNoticeOfAppeal();
    this.loadComments();
    this.loadConditionDefinitions();
    this.loadGeneralDocuments();
  }

  private loadNoticeOfAppeal() {
    this.caseService.getNoticeOfAppeal().subscribe({
      next: (res) => { this.appealNotice = res.noticeOfAppeal; },
      error: () => { /* ignore */ },
    });
  }

  private loadConditionDefinitions() {
    this.caseService.getConditionDefinitions().subscribe({
      next: (definitions) => {
        this.conditions = definitions.map(def => ({
          ...def,
          checked: false,
          documents: [],
          uploadState: 'idle' as const,
          uploadError: '',
        }));
        this.loadConditions();
      },
      error: () => { /* ignore */ },
    });
  }

  private loadConditions() {
    this.caseService.getConditions().subscribe({
      next: (res) => {
        for (const saved of res.conditions) {
          const condition = this.conditions.find(c => c.id === saved.conditionDefinitionId);
          if (condition) condition.checked = saved.checked;
        }
        this.loadConditionDocuments();
      },
      error: () => { /* ignore */ },
    });
  }

  private loadConditionDocuments() {
    for (const condition of this.conditions) {
      if (condition.checked) {
        this.caseService.getConditionDocuments(condition.id).subscribe({
          next: (res) => { condition.documents = res.documents; },
          error: () => { /* ignore */ },
        });
      }
    }
  }

  onConditionChange() {
    this.conditionsError = '';
    const payload = this.conditions.map(c => ({ conditionDefinitionId: c.id, checked: c.checked }));
    this.caseService.updateConditions(payload).subscribe({
      error: () => { this.conditionsError = 'Failed to save conditions. Please try again.'; },
    });
  }

  back() {
    this.router.navigate(['/sign']);
  }

  copyCase() {
    navigator.clipboard.writeText(this.caseNumber);
    this.copied = true;
    setTimeout(() => (this.copied = false), 2000);
  }

  onAppealNoticeChange(files: File[] | null) {
    const file = files?.[0] ?? null;
    if (!file) return;
    this.appealNoticeUploading = true;
    this.appealNoticeError = '';
    this.caseService.uploadNoticeOfAppeal(file).subscribe({
      next: (res) => {
        this.appealNotice = res.noticeOfAppeal;
        this.appealNoticeUploading = false;
      },
      error: () => {
        this.appealNoticeUploading = false;
        this.appealNoticeError = 'Upload failed. Please try again.';
      },
    });
  }

  removeAppealNotice() {
    this.appealNoticeError = '';
    this.caseService.deleteNoticeOfAppeal().subscribe({
      next: () => { this.appealNotice = null; },
      error: () => { this.appealNoticeError = 'Failed to remove file. Please try again.'; },
    });
  }

  onConditionFilesChange(condition: ConditionItem, files: File[] | null) {
    const fileList = files ?? [];
    if (fileList.length === 0) return;
    condition.uploadState = 'uploading';
    condition.uploadError = '';
    this.caseService.uploadConditionDocuments(condition.id, fileList).subscribe({
      next: () => {
        this.caseService.getConditionDocuments(condition.id).subscribe({
          next: (res) => {
            condition.documents = res.documents;
            condition.uploadState = 'idle';
          },
          error: () => {
            condition.uploadState = 'idle';
          },
        });
      },
      error: () => {
        condition.uploadState = 'error';
        condition.uploadError = 'Upload failed. Please try again.';
      },
    });
  }

  removeConditionDocument(condition: ConditionItem, doc: CaseDocument) {
    this.caseService.deleteDocument(doc.id).subscribe({
      next: () => {
        condition.documents = condition.documents.filter(d => d.id !== doc.id);
      },
      error: () => {
        condition.uploadState = 'error';
        condition.uploadError = 'Failed to remove file. Please try again.';
      },
    });
  }

  private loadGeneralDocuments() {
    this.caseService.getDocuments().subscribe({
      next: (res) => {
        this.generalDocuments = res.documents.filter(d => !d.conditionId);
      },
      error: () => { /* ignore */ },
    });
  }

  onGeneralFilesChange(files: File[] | null) {
    const fileList = files ?? [];
    if (fileList.length === 0) return;
    this.generalUploadState = 'uploading';
    this.generalUploadError = '';
    this.caseService.uploadDocuments(fileList).subscribe({
      next: () => {
        this.generalUploadState = 'idle';
        this.loadGeneralDocuments();
      },
      error: () => {
        this.generalUploadState = 'error';
        this.generalUploadError = 'Upload failed. Please try again.';
      },
    });
  }

  removeGeneralDocument(doc: CaseDocument) {
    this.caseService.deleteDocument(doc.id).subscribe({
      next: () => {
        this.generalDocuments = this.generalDocuments.filter(d => d.id !== doc.id);
      },
      error: () => {
        this.generalUploadError = 'Failed to remove file. Please try again.';
      },
    });
  }

  private loadComments() {
    this.caseService.getComments().subscribe({
      next: (res) => {
        this.comments = res.comments;
      },
      error: () => { /* endpoint may not be available yet */ },
    });
  }

  removeComment(comment: CaseComment) {
    this.commentError = '';
    this.caseService.deleteComment(comment.id).subscribe({
      next: () => {
        this.comments = this.comments.filter(c => c.id !== comment.id);
      },
      error: () => {
        this.commentError = 'Failed to remove comment. Please try again.';
      },
    });
  }

  submitConditions() {
    this.noteError = '';
    this.commentError = '';
    const trimmed = this.noteText.trim();
    if (trimmed.length > 10_000) {
      this.noteError = 'Comment must be under 10,000 characters.';
      return;
    }
    if (trimmed) {
      this.caseService.addComment(trimmed).subscribe({
        next: (comment) => {
          this.comments = [...this.comments, comment];
          this.noteText = '';
        },
        error: () => {
          this.commentError = 'Failed to save comment. Please try again.';
        },
      });
    }
    this.auth.updateProgress('your-case').subscribe({
      next: () => { /* update handled silently */ },
      error: () => { /* error handled silently */ },
    });
  }

  get chipLabel(): (step: TimelineStep) => string | null {
    return (step: TimelineStep) => {
      if (step.status === 'complete') return 'Complete';
      if (step.status === 'active') return 'In progress';
      return null;
    };
  }

  get chipColor(): (step: TimelineStep) => 'success' | 'secondary' {
    return (step: TimelineStep) => {
      return step.status === 'complete' ? 'success' : 'secondary';
    };
  }

  get chipIcon(): (step: TimelineStep) => string {
    return (step: TimelineStep) => {
      return step.status === 'complete' ? 'check' : 'clock';
    };
  }

}
